import React, { useState, useRef, useEffect } from 'react';
import { Button } from './shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './shadcn/card';
import { Input } from './shadcn/input';
import { Label } from './shadcn/label';
import { Alert, AlertDescription } from './shadcn/alert';
import { CheckCircle } from 'lucide-react';
import { useValidation, validationRules } from '../utils/useValidation';
import { sanitizeInput, sanitizeHTML, InputRateLimiter } from '../utils/securityValidation';
import { submitFormWithCSRF } from '../utils/csrf';
import { logSuspiciousActivity, logRateLimitExceeded } from '../utils/securityLogging';

function Contact() {
  const { values, errors, touched, handleChange, handleBlur, validateForm, reset } = useValidation(
    { name: '', email: '', message: '' },
    validationRules.contact
  );
  
  const [submitted, setSubmitted] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const [loading, setLoading] = useState(false);
  const contactAttempts = useRef(new InputRateLimiter(5, 60 * 60 * 1000)); // 5 attempts per hour

  // Check for excessive contact attempts
  useEffect(() => {
    const isAllowed = contactAttempts.current.isAllowed('contact');
    if (!isAllowed) {
      setSecurityError('Too many contact requests. Please try again later.');
      logRateLimitExceeded('/contact', 5);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Security check: Rate limiting
    const isAllowed = contactAttempts.current.isAllowed('contact');
    if (!isAllowed) {
      setSecurityError('Too many contact requests. Please try again later.');
      logRateLimitExceeded('/contact', 5);
      return;
    }
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Sanitize inputs
    const sanitizedData = {
      name: sanitizeInput(values.name),
      email: sanitizeInput(values.email),
      message: sanitizeHTML(values.message) // Allow some HTML for message
    };
    
    // Additional security validation
    if (!sanitizedData.name || !sanitizedData.email || !sanitizedData.message) {
      setSecurityError('Invalid input detected. Please check your entries.');
      logSuspiciousActivity('invalid_contact_form_data');
      return;
    }
    
    try {
      setLoading(true);
      setSecurityError('');
      
      // Submit form with CSRF protection
      const result = await submitFormWithCSRF('/api/contact', sanitizedData);
      
      if (result.success) {
        setSubmitted(true);
        reset();
      } else {
        setSecurityError(result.error || 'Failed to send message. Please try again.');
        logSuspiciousActivity('contact_form_submission_failed', {
          email: sanitizedData.email,
          messageLength: sanitizedData.message.length
        });
      }
    } catch (error) {
      setSecurityError('Failed to send message. Please try again.');
      logSuspiciousActivity('contact_form_exception', {
        email: sanitizedData.email,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Contact Support</CardTitle>
            <CardDescription>Have questions or need help? Send us a message.</CardDescription>
          </CardHeader>
          <CardContent>
            {submitted && (
              <Alert className="mb-6 bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Thank you for your message. Our support team will get back to you soon.
                </AlertDescription>
              </Alert>
            )}
            
            {securityError && (
              <Alert variant="destructive" className="mb-4">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{securityError}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your name"
                  value={values.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  onBlur={() => handleBlur('name')}
                  required
                  className={touched.name && errors.name ? 'border-red-500' : ''}
                />
                {touched.name && errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={values.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  required
                  className={touched.email && errors.email ? 'border-red-500' : ''}
                />
                {touched.email && errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <textarea
                  id="message"
                  placeholder="Enter your message"
                  value={values.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  onBlur={() => handleBlur('message')}
                  required
                  rows={5}
                  className={`w-full px-3 py-2 border rounded-md ${touched.message && errors.message ? 'border-red-500' : 'border-gray-300'}`}
                />
                {touched.message && errors.message && (
                  <p className="text-sm text-red-500">{errors.message}</p>
                )}
              </div>

              <Button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Contact;